import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Mail, Cookie } from 'lucide-react';
import { motion } from 'framer-motion';
export function PrivacyPolicy() {
  const sections = [{
    icon: <Database className="h-6 w-6" data-id="element-2115" />,
    title: 'What Information We Collect',
    content: [{
      subtitle: 'Information You Provide',
      items: ['Account information (name, email, password)', 'Profile information (pronouns, bio, location - all optional)', 'Course enrollment and participation data', 'Payment information (processed by Stripe - we never see your full card number)', 'Communications with us (emails, support requests, feedback)']
    }, {
      subtitle: 'Information Collected Automatically',
      items: ['Device and browser information', 'IP address and general location (city/state level)', 'Pages visited and time spent on site', 'Referral source (how you found us)', 'Technical data for debugging and security']
    }, {
      subtitle: "Information We Don't Collect",
      items: ['We do not collect weight, BMI, or health data', 'We do not track you across other websites', 'We do not sell or share your data with advertisers', 'We do not use facial recognition or biometric data']
    }]
  }, {
    icon: <Eye className="h-6 w-6" data-id="element-2116" />,
    title: 'How We Use Your Information',
    content: [{
      subtitle: 'To Provide Our Services',
      items: ['Create and manage your account', 'Process course enrollments and payments', 'Send course materials and updates', 'Provide customer support', 'Improve our platform and content']
    }, {
      subtitle: 'To Communicate With You',
      items: ['Send transactional emails (receipts, password resets)', 'Send course-related updates and announcements', 'Send our monthly newsletter (if you opt in)', 'Respond to your questions and feedback', 'Notify you of policy changes']
    }, {
      subtitle: 'To Maintain Security',
      items: ['Prevent fraud and abuse', 'Monitor for security threats', 'Enforce our Terms of Service', 'Comply with legal obligations']
    }]
  }, {
    icon: <Lock className="h-6 w-6" data-id="element-2117" />,
    title: 'How We Protect Your Information',
    content: [{
      subtitle: 'Technical Safeguards',
      items: ['SSL/TLS encryption for all data transmission', 'Encrypted storage of sensitive data', 'Regular security audits and updates', 'Secure password hashing (bcrypt)', 'Two-factor authentication available']
    }, {
      subtitle: 'Organizational Safeguards',
      items: ['Limited employee access to personal data', 'Background checks for team members with data access', 'Regular privacy and security training', 'Incident response plan for data breaches', 'Annual third-party security assessments']
    }]
  }, {
    icon: <Shield className="h-6 w-6" data-id="element-2118" />,
    title: 'Who We Share Your Information With',
    content: [{
      subtitle: 'Service Providers',
      text: 'We share limited data with trusted service providers who help us operate ReGrowth:',
      items: ['Stripe (payment processing)', 'SendGrid (email delivery)', 'Zoom (live course sessions)', 'AWS (hosting and storage)', 'Google Analytics (website analytics - anonymized)'],
      note: 'All service providers are contractually required to protect your data and use it only for the services they provide to us.'
    }, {
      subtitle: 'Legal Requirements',
      text: 'We may disclose information if required by law, such as:',
      items: ['In response to valid subpoenas or court orders', 'To comply with legal obligations', 'To protect our rights or property', 'To prevent harm to individuals'],
      note: 'We will notify you of legal requests unless prohibited by law.'
    }, {
      subtitle: 'We Never Share With',
      items: ['Advertisers or marketing companies', 'Data brokers', 'Social media platforms (unless you explicitly connect your account)', 'Anyone else without your explicit consent']
    }]
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-2119">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-100 py-6 sticky top-16 z-40" data-id="element-2120">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2121">
          <Link to="/policies" className="inline-flex items-center text-sm font-bold text-brand-navy hover:text-brand-cyan transition-colors" data-id="element-2122">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-2123" />
            Back to Policies
          </Link>
        </div>
      </header>

      {/* Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-2124">
        <div className="max-w-4xl mx-auto" data-id="element-2125">
          {/* Title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-12" data-id="element-2126">
            <span className="inline-block px-3 py-1 bg-brand-cyan text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-2127">
              Privacy Policy
            </span>

            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6 text-brand-black" data-id="element-2128">
              Privacy Policy
            </h1>

            <p className="text-lg text-gray-600 mb-4" data-id="element-2129">
              Last updated: January 15, 2024
            </p>

            <div className="prose prose-lg" data-id="element-2130">
              <p className="text-xl text-gray-700 leading-relaxed" data-id="element-2131">
                At ReGrowth, we believe privacy is a right, not a privilege.
                This policy explains what information we collect, how we use it,
                and your rights regarding your data.
              </p>
            </div>
          </motion.div>

          {/* TL;DR */}
          <section className="mb-16" data-id="element-2132">
            <div className="bg-brand-cyan/10 border-l-4 border-brand-cyan p-8" data-id="element-2133">
              <h2 className="text-2xl font-black uppercase mb-4 text-brand-black" data-id="element-2134">
                TL;DR
              </h2>
              <ul className="space-y-2 text-gray-700" data-id="element-2135">
                <li data-id="element-2136">• We collect only what we need to provide our services</li>
                <li data-id="element-2137">
                  • We never sell your data to advertisers or third parties
                </li>
                <li data-id="element-2138">• You can download or delete your data at any time</li>
                <li data-id="element-2139">• We use industry-standard security measures</li>
                <li data-id="element-2140">• We're transparent about what we collect and why</li>
              </ul>
            </div>
          </section>

          {/* Main Sections */}
          {sections.map((section, index) => <motion.section key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="mb-16" data-id="element-2141">
              <div className="flex items-center gap-3 mb-6" data-id="element-2142">
                <div className="text-brand-cyan" data-id="element-2143">{section.icon}</div>
                <h2 className="text-3xl font-black uppercase text-brand-black" data-id="element-2144">
                  {section.title}
                </h2>
              </div>

              <div className="space-y-8" data-id="element-2145">
                {section.content.map((subsection, i) => <div key={i} className="bg-white border-2 border-brand-black p-6" data-id="element-2146">
                    <h3 className="text-xl font-bold mb-4 text-brand-navy" data-id="element-2147">
                      {subsection.subtitle}
                    </h3>
                    {subsection.text && <p className="text-gray-700 mb-4" data-id="element-2148">{subsection.text}</p>}
                    <ul className="space-y-2" data-id="element-2149">
                      {subsection.items.map((item, j) => <li key={j} className="flex items-start text-gray-700" data-id="element-2150">
                          <span className="text-brand-cyan mr-3 mt-1" data-id="element-2151">•</span>
                          <span data-id="element-2152">{item}</span>
                        </li>)}
                    </ul>
                    {subsection.note && <p className="text-sm text-gray-600 mt-4 italic border-t border-gray-100 pt-4" data-id="element-2153">
                        {subsection.note}
                      </p>}
                  </div>)}
              </div>
            </motion.section>)}

          {/* Cookies */}
          <section className="mb-16" data-id="element-2154">
            <div className="flex items-center gap-3 mb-6" data-id="element-2155">
              <Cookie className="h-6 w-6 text-brand-cyan" data-id="element-2156" />
              <h2 className="text-3xl font-black uppercase text-brand-black" data-id="element-2157">
                Cookies & Tracking
              </h2>
            </div>

            <div className="bg-white border-2 border-brand-black p-6 mb-6" data-id="element-2158">
              <h3 className="text-xl font-bold mb-4 text-brand-navy" data-id="element-2159">
                What Are Cookies?
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2160">
                Cookies are small text files stored on your device that help
                websites remember your preferences and improve your experience.
              </p>

              <h4 className="font-bold text-brand-cyan mb-3 mt-6" data-id="element-2161">
                Essential Cookies (Required)
              </h4>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-2162">
                <li className="flex items-start" data-id="element-2163">
                  <span className="text-brand-cyan mr-3" data-id="element-2164">•</span>
                  <span data-id="element-2165">Session cookies (keep you logged in)</span>
                </li>
                <li className="flex items-start" data-id="element-2166">
                  <span className="text-brand-cyan mr-3" data-id="element-2167">•</span>
                  <span data-id="element-2168">Security cookies (prevent fraud)</span>
                </li>
                <li className="flex items-start" data-id="element-2169">
                  <span className="text-brand-cyan mr-3" data-id="element-2170">•</span>
                  <span data-id="element-2171">Preference cookies (remember your settings)</span>
                </li>
              </ul>

              <h4 className="font-bold text-brand-cyan mb-3" data-id="element-2172">
                Analytics Cookies (Optional)
              </h4>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-2173">
                <li className="flex items-start" data-id="element-2174">
                  <span className="text-brand-cyan mr-3" data-id="element-2175">•</span>
                  <span data-id="element-2176">Google Analytics (anonymized - no personal data)</span>
                </li>
                <li className="flex items-start" data-id="element-2177">
                  <span className="text-brand-cyan mr-3" data-id="element-2178">•</span>
                  <span data-id="element-2179">Help us understand how people use our site</span>
                </li>
              </ul>

              <p className="text-sm text-gray-600 bg-gray-50 p-4 border-l-4 border-brand-cyan" data-id="element-2180">
                You can disable analytics cookies in your browser settings or
                through our cookie preferences (available in the footer).
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-16" data-id="element-2181">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-2182">
              Your Privacy Rights
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2183">
              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2184">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2185">
                  Access Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2186">
                  Request a copy of all personal data we have about you.
                </p>
                <p className="text-xs text-gray-600" data-id="element-2187">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-2188">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2189">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2190">
                  Correct Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2191">
                  Update or correct inaccurate information in your account.
                </p>
                <p className="text-xs text-gray-600" data-id="element-2192">
                  Available in Account Settings
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2193">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2194">
                  Delete Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2195">
                  Request permanent deletion of your account and data.
                </p>
                <p className="text-xs text-gray-600" data-id="element-2196">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-2197">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2198">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2199">
                  Export Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2200">
                  Download your data in a portable format (JSON, CSV).
                </p>
                <p className="text-xs text-gray-600" data-id="element-2201">
                  Available in Account Settings
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2202">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2203">
                  Opt Out of Marketing
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2204">
                  Unsubscribe from newsletters and promotional emails.
                </p>
                <p className="text-xs text-gray-600" data-id="element-2205">
                  Click "unsubscribe" in any email
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2206">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-2207">
                  Object to Processing
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2208">
                  Object to certain uses of your data (e.g., analytics).
                </p>
                <p className="text-xs text-gray-600" data-id="element-2209">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-2210">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>
            </div>

            <div className="mt-6 bg-brand-cyan/10 border-l-4 border-brand-cyan p-6" data-id="element-2211">
              <p className="text-sm text-gray-700" data-id="element-2212">
                <strong data-id="element-2213">Response Time:</strong> We respond to all privacy
                requests within 30 days. For complex requests, we may extend
                this to 60 days and will notify you.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-16" data-id="element-2214">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-2215">
              How Long We Keep Your Data
            </h2>

            <div className="prose prose-lg max-w-none" data-id="element-2216">
              <div className="bg-white border-2 border-brand-black p-6" data-id="element-2217">
                <ul className="space-y-4 text-gray-700" data-id="element-2218">
                  <li className="flex items-start" data-id="element-2219">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-2220">
                      Account data:
                    </span>
                    <span data-id="element-2221">
                      Until you delete your account, then 30 days for backup
                      purposes
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2222">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-2223">
                      Course data:
                    </span>
                    <span data-id="element-2224">
                      For the duration of your enrollment plus 1 year for
                      records
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2225">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-2226">
                      Payment data:
                    </span>
                    <span data-id="element-2227">7 years (required by law for tax purposes)</span>
                  </li>
                  <li className="flex items-start" data-id="element-2228">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-2229">
                      Analytics data:
                    </span>
                    <span data-id="element-2230">
                      26 months (Google Analytics default), then automatically
                      deleted
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2231">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-2232">
                      Support emails:
                    </span>
                    <span data-id="element-2233">3 years for reference and quality improvement</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-16" data-id="element-2234">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-2235">
              Children's Privacy
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-2236">
              <p className="text-gray-700 mb-4" data-id="element-2237">
                ReGrowth is not intended for children under 16. We do not
                knowingly collect personal information from children under 16.
              </p>
              <p className="text-gray-700" data-id="element-2238">
                If you believe we have collected information from a child under
                16, please contact us immediately at{' '}
                <a href="mailto:privacy@regrowth.com" className="text-brand-cyan font-bold hover:underline" data-id="element-2239">
                  privacy@regrowth.com
                </a>{' '}
                and we will delete it.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section className="mb-16" data-id="element-2240">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-2241">
              International Users
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-2242">
              <p className="text-gray-700 mb-4" data-id="element-2243">
                ReGrowth is based in the United States. If you access our
                services from outside the U.S., your data will be transferred to
                and processed in the U.S.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-2244">
                <strong data-id="element-2245">For EU/UK users:</strong> We comply with GDPR
                requirements, including:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-2246">
                <li className="flex items-start" data-id="element-2247">
                  <span className="text-brand-cyan mr-3" data-id="element-2248">•</span>
                  <span data-id="element-2249">Standard Contractual Clauses for data transfers</span>
                </li>
                <li className="flex items-start" data-id="element-2250">
                  <span className="text-brand-cyan mr-3" data-id="element-2251">•</span>
                  <span data-id="element-2252">
                    Your right to lodge a complaint with your data protection
                    authority
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2253">
                  <span className="text-brand-cyan mr-3" data-id="element-2254">•</span>
                  <span data-id="element-2255">
                    All GDPR rights listed in "Your Privacy Rights" above
                  </span>
                </li>
              </ul>
              <p className="text-gray-700" data-id="element-2256">
                <strong data-id="element-2257">For California users:</strong> See our California
                Privacy Notice below for CCPA-specific rights.
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-16" data-id="element-2258">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-2259">
              Changes to This Policy
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-2260">
              <p className="text-gray-700 mb-4" data-id="element-2261">
                We may update this Privacy Policy from time to time. When we
                make significant changes:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-2262">
                <li className="flex items-start" data-id="element-2263">
                  <span className="text-brand-cyan mr-3" data-id="element-2264">•</span>
                  <span data-id="element-2265">
                    We'll email you at least 30 days before changes take effect
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2266">
                  <span className="text-brand-cyan mr-3" data-id="element-2267">•</span>
                  <span data-id="element-2268">We'll post a notice on our website</span>
                </li>
                <li className="flex items-start" data-id="element-2269">
                  <span className="text-brand-cyan mr-3" data-id="element-2270">•</span>
                  <span data-id="element-2271">
                    We'll update the "Last updated" date at the top of this page
                  </span>
                </li>
              </ul>
              <p className="text-gray-700" data-id="element-2272">
                Continued use of ReGrowth after changes take effect means you
                accept the updated policy.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16" data-id="element-2273">
            <div className="bg-brand-navy text-white p-8 border-2 border-brand-black" data-id="element-2274">
              <div className="flex items-start gap-4" data-id="element-2275">
                <Mail className="h-8 w-8 flex-shrink-0 mt-1" data-id="element-2276" />
                <div data-id="element-2277">
                  <h2 className="text-2xl font-black uppercase mb-4" data-id="element-2278">
                    Questions About Privacy?
                  </h2>
                  <p className="text-gray-200 mb-4" data-id="element-2279">
                    If you have questions about this Privacy Policy or how we
                    handle your data:
                  </p>
                  <div className="space-y-2 text-gray-200" data-id="element-2280">
                    <p data-id="element-2281">
                      <strong data-id="element-2282">Email:</strong>{' '}
                      <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-2283">
                        privacy@regrowth.com
                      </a>
                    </p>
                    <p data-id="element-2284">
                      <strong data-id="element-2285">Mail:</strong> ReGrowth Privacy Team, [Address]
                    </p>
                    <p data-id="element-2286">
                      <strong data-id="element-2287">Response time:</strong> Within 2 business days
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t-2 border-gray-200" data-id="element-2288">
            <p className="text-sm text-gray-600 mb-4" data-id="element-2289">
              This privacy policy was last reviewed and updated on January 15,
              2024.
            </p>
            <p className="text-sm text-gray-600" data-id="element-2290">
              <strong data-id="element-2291">Version:</strong> 2.0 |{' '}
              <Link to="/policies" className="text-brand-cyan hover:underline" data-id="element-2292">
                View all policies
              </Link>
            </p>
          </div>
        </div>
      </article>
    </div>;
}